/**
 * The forms API calls (forms card 9; server: /api/forms,
 * routes/non_generated_single_table_business_logic_routes/forms-routes.ts) and their answer
 * shapes, as services/forms/form-serialize.ts and src/data/forms*.ts write them. Plain functions
 * over httpClient, one per endpoint; the hooks in ./useForms.ts are the only callers. Every JSON
 * answer is `{ success, data, … }`. Definitions, statuses and the answers schema come from
 * @aiga/app-config/forms, not from here.
 */
import { HttpClientError, httpClient } from '@/api/http-client';
import { env } from '@/lib/env';
import type {
  FormAnswerValue,
  FormAnswers,
  FormCapability,
  FormDefinition,
  FormDefinitionComparison,
  FormItemStatus,
  FormNotifyRules,
  FormSubmissionStatus,
  FormTargetKind,
  FormVersionStatus,
} from '@aiga/app-config/forms';

export interface FormPerson {
  id: string;
  name: string;
}

/** A record as the screens show it (its adapter's label and the web path of its page). */
export interface FormTargetRef {
  kind: FormTargetKind;
  id: string;
  label: string;
  link: string;
}

/** GET /forms/catalog: a type the caller may submit or process, with its published definition. */
export interface FormCatalogType {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  owner_department: { id: string; name: string } | null;
  number_prefix: string;
  allowed_target_kinds: FormTargetKind[];
  target_required: boolean;
  is_active: boolean;
  sort_order: number;
  capabilities: FormCapability[];
  /** SUBMIT, active and published. */
  can_submit: boolean;
  can_process: boolean;
  /** The PUBLISHED version; null = none yet. */
  version: { id: string; version: number; published_at: string | null; definition: FormDefinition } | null;
}

/** GET /forms/catalog?summary=1: a catalog type without its definition (the sidebar, the listing screens). */
export type FormCatalogSummaryType = Omit<FormCatalogType, 'version'> & {
  version: Omit<NonNullable<FormCatalogType['version']>, 'definition'> | null;
};

export interface FormsCatalog {
  types: FormCatalogType[];
  /** Only when asked for a record. */
  target: FormTargetRef | null;
}

/** `{source, value}` per pre-filled field key. */
export type FormPrefilled = Record<string, { source: string; value: FormAnswerValue }>;

export interface FormSubmissionItem {
  id: string;
  part_key: string;
  /** The part's label in the definition. */
  label: string;
  sort_order: number;
  status: FormItemStatus;
  stage_key: string | null;
  answers: FormAnswers;
  prefilled: FormPrefilled;
  assignee: FormPerson | null;
  sent_at: string | null;
  in_review_at: string | null;
  done_at: string | null;
  cancelled_at: string | null;
}

export interface FormSubmissionEvent {
  id: string;
  item_id: string | null;
  event_type: string;
  details: Record<string, unknown>;
  actor: FormPerson | null;
  created_at: string;
}

/** One submission (GET /forms/submissions/:id and every write's answer). Restricted answers are left out unless `access.view_restricted`. */
export interface FormSubmission {
  id: string;
  form_type: { id: string; key: string; name: string; number_prefix: string; is_active: boolean };
  version: { id: string; version: number };
  number: number | null;
  number_display: string | null;
  title: string | null;
  status: FormSubmissionStatus;
  status_label: string;
  stage_key: string | null;
  /** Sent back as `if_revision`. */
  revision: number;
  answers: FormAnswers;
  prefilled: FormPrefilled;
  requested_by: FormPerson | null;
  requested_of: FormPerson | null;
  assignee: FormPerson | null;
  submitted_by: FormPerson | null;
  due_on: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  /** The pinned definition. */
  definition: FormDefinition;
  /** A target the caller may not see has no label / link. */
  targets: Array<{ id: string; kind: FormTargetKind; record_id: string; is_primary: boolean; label?: string; link?: string }>;
  items: FormSubmissionItem[];
  events: FormSubmissionEvent[];
  capabilities: FormCapability[];
  access: { fill: boolean; process: boolean; view_restricted: boolean };
  /** Restricted fields exist and their values were left out for this caller. */
  restricted_hidden: boolean;
}

/** A narrow list row (no answers): the submissions on a record, mine, the inbox. */
export interface FormSubmissionListRow {
  id: string;
  form_type: { id: string; key: string; name: string };
  number: number | null;
  number_display: string | null;
  title: string | null;
  status: FormSubmissionStatus;
  stage_key: string | null;
  requested_by: FormPerson | null;
  requested_of: FormPerson | null;
  assignee: FormPerson | null;
  due_on: string | null;
  submitted_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  /** The part chips, in order. */
  parts: Array<{ part_key: string; status: FormItemStatus }>;
}

export type MyFormRow = FormSubmissionListRow & { waiting_on_me: boolean };

export interface FormFile {
  id: string;
  /** The FILE field it is on. */
  field_key: string;
  /** The part the field belongs to; null = a shared field. */
  part_key: string | null;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: FormPerson | null;
  created_at: string;
  archived_at: string | null;
}

/** A live file as the files list answers it: with a 1-hour signed URL (null when Storage could not sign it). */
export type FormListedFile = FormFile & { url: string | null };

export interface FormFiles {
  files: FormListedFile[];
  archived_files: FormFile[];
}

export interface FormFileUrl {
  url: string;
  file_name: string;
  mime_type: string | null;
  expires_in: number;
}

export interface CreateFormSubmissionBody {
  form_type: string;
  /** The first is the primary target. */
  targets?: Array<{ kind: FormTargetKind; id: string }>;
  parts?: string[];
}

/** A merge patch: a key sent replaces the answer, null clears it, a key not sent is kept. */
export type FormAnswersPatch = Record<string, FormAnswerValue | null>;

export interface SaveFormSubmissionBody {
  if_revision: number;
  answers?: FormAnswersPatch;
  parts?: Record<string, FormAnswersPatch>;
}

/** The filters the inbox and the export share. */
export interface FormListFilters {
  form_type?: string;
  status?: FormSubmissionStatus[];
  stage?: string;
  /** 'me', 'none' or a person's id. */
  assignee?: string;
  q?: string;
  /** `<field_key>:<eq|gte|lte>:<value>`, reportable fields only. */
  filter?: string[];
}

export interface FormsInboxPage {
  data: FormSubmissionListRow[];
  counts: Record<'SUBMITTED' | 'IN_PROGRESS' | 'RETURNED', number>;
  next_cursor: string | null;
}

// ── Settings → Forms (card 13; services/forms/form-type-writes.ts) ──

export interface FormRef {
  id: string;
  name: string;
}

/** One capability to exactly one grantee. */
export interface FormTypeGrant {
  id: string;
  capability: FormCapability;
  role: FormRef | null;
  department: FormRef | null;
  feature_key: string | null;
}

export interface FormTypeVersionRow {
  id: string;
  version: number;
  status: FormVersionStatus;
  published_at: string | null;
  published_by: FormPerson | null;
}

/** GET /forms/types: a type as Settings → Forms edits it. */
export interface FormManagedType {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  owner_department: FormRef | null;
  number_prefix: string;
  allowed_target_kinds: FormTargetKind[];
  target_required: boolean;
  notify_rules: FormNotifyRules;
  is_active: boolean;
  sort_order: number;
  updated_at: string;
  capabilities: FormCapability[];
  grants: FormTypeGrant[];
  /** Newest first, the builder's draft among them (status DRAFT). */
  versions: FormTypeVersionRow[];
  /** The builder's draft, when one is open (its definition: formsApi.draft). */
  draft: { id: string; version: number; updated_at: string } | null;
  /** The PUBLISHED version's definition (null = none yet): the file the next version starts from. */
  published_definition: FormDefinition | null;
  submission_count: number;
}

export interface FormsManage {
  types: FormManagedType[];
  roles: FormRef[];
  departments: FormRef[];
}

export interface CreateFormTypeBody {
  key: string;
  name: string;
  number_prefix: string;
  description?: string | null;
  category?: string | null;
  owner_department_id?: string | null;
  allowed_target_kinds?: FormTargetKind[];
  target_required?: boolean;
  sort_order?: number;
}

export interface UpdateFormTypeBody {
  name?: string;
  description?: string | null;
  category?: string | null;
  owner_department_id?: string | null;
  allowed_target_kinds?: FormTargetKind[];
  target_required?: boolean;
  is_active?: boolean;
  sort_order?: number;
  notify_rules?: FormNotifyRules;
}

/** Exactly one of role_id, department_id, feature_key. */
export type FormTypeGrantBody = { capability: FormCapability } & ({ role_id: string } | { department_id: string } | { feature_key: string });

/** POST /forms/types/:id/versions/check: the definition against the rules and the published version. */
export interface FormVersionCheck {
  ok: boolean;
  errors: string[];
  current_version: number | null;
  next_version: number;
  comparison: FormDefinitionComparison | null;
  unchanged: boolean;
}

/** GET / PUT /forms/types/:id/versions/draft: the builder's draft, any object until it is published. */
export interface FormTypeDraft {
  id: string;
  /** The number it publishes as. */
  version: number;
  definition: unknown;
  /** What the next save sends as `if_updated_at`. */
  updated_at: string;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

const BASE = '/forms';

/** `?a=1&b=2&b=3` from the defined, non-empty values; a list repeats its key ('' when none). */
export function formsQuery(params: Record<string, string | number | string[] | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    for (const v of Array.isArray(value) ? value : [value]) {
      if (v === undefined || v === null || v === '') continue;
      q.append(key, String(v));
    }
  }
  const text = q.toString();
  return text ? `?${text}` : '';
}

const data = async <T>(p: Promise<Envelope<T>>): Promise<T> => (await p).data;

/** A non-JSON call (multipart upload, CSV) through fetch with the session cookie; a non-2xx throws HttpClientError like httpClient. */
async function failed(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  throw new HttpClientError(res.status, body.error || fallback, body.code || 'UNKNOWN_ERROR', body);
}

/** Every call names its httpClient method and whole path, so the URL contract test reads each one. */
export const formsApi = {
  // ── Catalog ──
  catalog: async (target?: { kind: FormTargetKind; id: string }): Promise<FormsCatalog> => {
    const res = await httpClient.get<Envelope<FormCatalogType[]> & { target?: FormTargetRef }>(
      `${BASE}/catalog${formsQuery({ target_kind: target?.kind, target_id: target?.id })}`,
    );
    return { types: res.data, target: res.target ?? null };
  },
  catalogSummary: () => data(httpClient.get<Envelope<FormCatalogSummaryType[]>>(`${BASE}/catalog?summary=1`)),

  // ── Submissions ──
  create: (body: CreateFormSubmissionBody) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions`, body)),
  mine: () => data(httpClient.get<Envelope<MyFormRow[]>>(`${BASE}/submissions/mine`)),
  onTarget: async (kind: FormTargetKind, id: string): Promise<{ target: FormTargetRef; submissions: FormSubmissionListRow[] }> => {
    const res = await httpClient.get<Envelope<FormSubmissionListRow[]> & { target: FormTargetRef }>(
      `${BASE}/submissions${formsQuery({ target_kind: kind, target_id: id })}`,
    );
    return { target: res.target, submissions: res.data };
  },
  get: (id: string) => data(httpClient.get<Envelope<FormSubmission>>(`${BASE}/submissions/${id}`)),
  save: (id: string, body: SaveFormSubmissionBody) => data(httpClient.patch<Envelope<FormSubmission>>(`${BASE}/submissions/${id}`, body)),
  addPart: (id: string, partKey: string, ifRevision?: number) =>
    data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/parts`, { part_key: partKey, if_revision: ifRevision })),
  removePart: (id: string, partKey: string, ifRevision?: number) =>
    data(httpClient.delete<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/parts/${partKey}${formsQuery({ if_revision: ifRevision })}`)),
  /** Ask someone to fill it in; null = nobody. */
  ask: (id: string, userProfileId: string | null) =>
    data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/ask`, { user_profile_id: userProfileId })),
  submit: (id: string, ifRevision: number) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/submit`, { if_revision: ifRevision })),
  sendBack: (id: string, note: string) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/return`, { note })),
  cancel: (id: string, reason?: string | null) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/cancel`, { reason: reason ?? null })),
  archive: (id: string) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/archive`, {})),

  // ── Processing ──
  inbox: async (filters: FormListFilters & { cursor?: string | null; limit?: number }): Promise<FormsInboxPage> => {
    const { data: rows, counts, next_cursor } = await httpClient.get<Envelope<FormSubmissionListRow[]> & Omit<FormsInboxPage, 'data'>>(
      `${BASE}/inbox${formsQuery({ ...filters })}`,
    );
    return { data: rows, counts, next_cursor };
  },
  /** Take (`if_unassigned`: 409 FORM_ALREADY_ASSIGNED when someone else has it) or assign; null = nobody. */
  assign: (id: string, userProfileId: string | null, ifUnassigned?: boolean) =>
    data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/assign`, { user_profile_id: userProfileId, if_unassigned: ifUnassigned })),
  moveStage: (id: string, stageKey: string | null) =>
    data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/stage`, { stage_key: stageKey })),
  movePart: (id: string, partKey: string, body: { status?: FormItemStatus; stage_key?: string | null }) =>
    data(httpClient.patch<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/parts/${partKey}`, body)),
  complete: (id: string) => data(httpClient.post<Envelope<FormSubmission>>(`${BASE}/submissions/${id}/complete`, {})),
  /** A type's submissions as CSV (the inbox's filters; status any but DRAFT). */
  exportCsv: async (filters: FormListFilters & { form_type: string }): Promise<{ blob: Blob; file_name: string }> => {
    const res = await fetch(`${env.API_URL || ''}${BASE}/export${formsQuery({ ...filters })}`, { credentials: 'include' });
    if (!res.ok) return failed(res, 'Export failed');
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
    return { blob: await res.blob(), file_name: name ?? `${filters.form_type}.csv` };
  },

  // ── Files ──
  files: (id: string) => data(httpClient.get<Envelope<FormFiles>>(`${BASE}/submissions/${id}/files`)),
  /** The field key goes before the file: the API reads the fields sent ahead of the file part. */
  uploadFile: async (id: string, fieldKey: string, file: File): Promise<FormFile> => {
    const form = new FormData();
    form.append('field_key', fieldKey);
    form.append('file', file);
    const res = await fetch(`${env.API_URL || ''}${BASE}/submissions/${id}/files`, { method: 'POST', body: form, credentials: 'include' });
    if (!res.ok) return failed(res, 'Upload failed');
    return ((await res.json()) as Envelope<FormFile>).data;
  },
  fileUrl: (id: string, fileId: string) => data(httpClient.get<Envelope<FormFileUrl>>(`${BASE}/submissions/${id}/files/${fileId}/url`)),
  removeFile: (id: string, fileId: string) =>
    data(httpClient.delete<Envelope<{ id: string; field_key: string; storage_object_removed: boolean }>>(`${BASE}/submissions/${id}/files/${fileId}`)),
  archiveFile: (id: string, fileId: string) => data(httpClient.post<Envelope<FormFile>>(`${BASE}/submissions/${id}/files/${fileId}/archive`, {})),

  // ── Settings → Forms ──
  manage: async (): Promise<FormsManage> => {
    const res = await httpClient.get<Envelope<FormManagedType[]> & Omit<FormsManage, 'types'>>(`${BASE}/types`);
    return { types: res.data, roles: res.roles, departments: res.departments };
  },
  createType: (body: CreateFormTypeBody) => data(httpClient.post<Envelope<FormManagedType>>(`${BASE}/types`, body)),
  updateType: (id: string, body: UpdateFormTypeBody) => data(httpClient.patch<Envelope<FormManagedType>>(`${BASE}/types/${id}`, body)),
  /** An admin, a form never published nor used (409 FORM_TYPE_IN_USE otherwise: switch it off instead). */
  deleteType: (id: string) => data(httpClient.delete<Envelope<{ id: string; key: string }>>(`${BASE}/types/${id}`)),
  addGrant: (id: string, body: FormTypeGrantBody) => data(httpClient.post<Envelope<FormManagedType>>(`${BASE}/types/${id}/grants`, body)),
  removeGrant: (id: string, grantId: string) => data(httpClient.delete<Envelope<FormManagedType>>(`${BASE}/types/${id}/grants/${grantId}`)),
  checkVersion: (id: string, definition: unknown) =>
    data(httpClient.post<Envelope<FormVersionCheck>>(`${BASE}/types/${id}/versions/check`, { definition })),
  /** `ifCurrentVersion`: the published version the check ran against (null = none). */
  publishVersion: (id: string, definition: unknown, ifCurrentVersion: number | null) =>
    data(
      httpClient.post<Envelope<{ type: FormManagedType; version: { id: string; version: number } }>>(`${BASE}/types/${id}/versions`, {
        definition,
        if_current_version: ifCurrentVersion,
      }),
    ),
  draft: (id: string) => data(httpClient.get<Envelope<FormTypeDraft | null>>(`${BASE}/types/${id}/versions/draft`)),
  /** `ifUpdatedAt`: the draft's updated_at the screen holds (null = none; left out = overwrite). 409 FORM_DRAFT_STALE otherwise. */
  saveDraft: (id: string, definition: unknown, ifUpdatedAt?: string | null) =>
    data(
      httpClient.put<Envelope<FormTypeDraft>>(`${BASE}/types/${id}/versions/draft`, {
        definition,
        ...(ifUpdatedAt === undefined ? {} : { if_updated_at: ifUpdatedAt }),
      }),
    ),
  discardDraft: (id: string) => data(httpClient.delete<Envelope<{ id: string; version: number }>>(`${BASE}/types/${id}/versions/draft`)),
};
